import { DatePipe } from '@angular/common';
import {
  Inject,
  LOCALE_ID,
  Optional,
  Pipe,
  PipeTransform,
} from '@angular/core';
import { SERVICE_TIME_ZONE_OFFSET } from '@app/@shared/utils/service-time-zone';

/**
 * Formats a service-slot timestamp (pickup/delivery window start/end).
 *
 * Timezone resolution:
 * 1. A brand-level fixed service offset (`environment.serviceTimeZoneOffset`) always wins.
 * 2. Otherwise the offset parsed from `offsetDate` (e.g. `...+03:00`), like `flightDate`.
 * 3. Otherwise the device timezone (default DatePipe behaviour).
 *
 * Use `flightDate` for flight departure/arrival times, not this pipe.
 */
@Pipe({
  name: 'serviceDate',
  standalone: false,
})
export class ServiceDatePipe implements PipeTransform {
  private datePipe: DatePipe;

  constructor(
    @Inject(LOCALE_ID) locale: string,
    @Optional()
    @Inject(SERVICE_TIME_ZONE_OFFSET)
    private serviceTimeZoneOffset?: string | null,
  ) {
    this.datePipe = new DatePipe(locale);
  }

  transform(
    timestamp: number | string | null | undefined,
    format: string,
    offsetDate?: string | null,
  ): string | null {
    const value = timestamp ?? offsetDate;
    if (!value) {
      return null;
    }

    return this.datePipe.transform(value, format, this.getTimezone(offsetDate));
  }

  private getTimezone(offsetDate?: string | null): string | undefined {
    if (this.serviceTimeZoneOffset) {
      return this.serviceTimeZoneOffset;
    }

    if (!offsetDate) {
      return undefined;
    }

    const match = offsetDate.match(/(Z|[+-]\d{2}:?\d{2})$/);
    if (!match) {
      return undefined;
    }

    if (match[1] === 'Z') {
      return 'UTC';
    }

    return match[1].replace(':', '');
  }
}
